import { Menu } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { usePageStoreContext } from "../../../context/PageStoreContext";

type MenuItem = {
  key: string;
  label: React.ReactNode;
  permissions?: string[];
  children?: MenuItem[];
};

const rawItems = [
  {
    label: <Link to="/dashboard">Dashboard</Link>,
    key: "/dashboard",
    permissions: ["DASHBOARD"],
  },
  {
    label: (
      <span>
        Data Management <DownOutlined style={{ marginLeft: 4 }} />
      </span>
    ),
    key: "Data Management",
    permissions: ["SUPPLIER", "BOOKING_SUPPLIER_COST", "INVOICE"],
    children: [
      {
        key: "/supplier",
        label: <Link to="/supplier">Supplier</Link>,
        permissions: ["SUPPLIER"],
      },
      {
        key: "/booking-supplier-cost",
        label: <Link to="/booking-supplier-cost">Booking Supplier Cost</Link>,
        permissions: ["BOOKING_SUPPLIER_COST"],
      },
      {
        key: "/expect-supplier-payment",
        label: <Link to="/expect-supplier-payment">Expected Supplier Payment</Link>,
        permissions: ["BOOKING_SUPPLIER_COST"],
      },
      {
        key: "/invoice",
        label: <Link to="/invoice">Invoice</Link>,
        permissions: ["INVOICE"],
      },
      {
        key: "/supplier-invoice-record",
        label: <Link to="/supplier-invoice-record">Supplier Invoice Record</Link>,
        permissions: ["INVOICE"],
      },
      {
        key: "/supplier-invoice-record-flight",
        label: <Link to="/supplier-invoice-record-flight">Supplier Invoice Records - Flights</Link>,
        permissions: ["INVOICE"],
      },
    ],
  },
  {
    label: (
      <span>
        Workflow <DownOutlined style={{ marginLeft: 4 }} />
      </span>
    ),
    key: "Workflow",
    permissions: ["WORKFLOW"],
    children: [
      {
        key: "/ap-workflow",
        label: <Link to="/ap-workflow">AP Workflow</Link>,
        permissions: ["WORKFLOW"],
      },
      {
        key: "/flight-workflow",
        label: <Link to="/flight-workflow">Flight Workflow</Link>,
        permissions: ["WORKFLOW"],
      },
      {
        key: "/tour-event-workflow",
        label: <Link to="/tour-event-workflow">Tour - Event Workflow</Link>,
        permissions: ["WORKFLOW"],
      },
      {
        key: "/other-workflow",
        label: <Link to="/other-workflow">Other Workflow</Link>,
        permissions: ["WORKFLOW"],
      },
    ],
  },
  {
    label: <Link to="/financial-reporting">Financial Reporting</Link>,
    key: "/financial-reporting",
    permissions: ["FINANCIAL_REPORT"],
  },
  {
    label: (
      <span>
        Settings <DownOutlined style={{ marginLeft: 4 }} />
      </span>
    ),
    key: "settings",
    permissions: ["PERMISSION", "SYSTEM_DEFAULT"],
    children: [
      {
        key: "/permission",
        label: <Link to="/permission">Permission</Link>,
        permissions: ["PERMISSION"],
      },
      {
        key: "/system-default",
        label: <Link to="/system-default">System Default</Link>,
        permissions: ["SYSTEM_DEFAULT"],
      },
      {
        key: "/master-data",
        label: <Link to="/master-data">Master Data</Link>,
        permissions: ["MASTER_DATA"],
      },
    ],
  },
];

const MenuFM: React.FC = () => {
  const location = useLocation();
  const { clearAll } = usePageStoreContext();
  const hasAccess = useHasPermission;
  const filterMenuItems = (items: MenuItem[]): MenuItem[] =>
    items
      .map((item) => {
        if (item.children) {
          const filteredChildren = filterMenuItems(item.children);
          return hasAccess(item.permissions) || filteredChildren.length
            ? { ...item, children: filteredChildren }
            : null;
        }
        return hasAccess(item.permissions) ? item : null;
      })
      .filter((i): i is MenuItem => Boolean(i));

  const filteredItems = filterMenuItems(rawItems);

  const handleClick = () => {
    clearAll();
  };

  return (
    <Menu
      selectedKeys={[location.pathname]}
      mode="horizontal"
      items={filteredItems}
      className="menu"
      onClick={handleClick}
    />
  );
};

export default MenuFM;
